import videojs from 'video.js';

const _enablePlayPause = new WeakMap();
const _enableFullScreen = new WeakMap();
const _enableSpeedControl = new WeakMap();
const _enableSeeking = new WeakMap();
const _playbackRates = new WeakMap();
const _enablePIP = new WeakMap();

/**
 * The Settings module.
 * @member {SettingsPlugin} settings
 * @memberof MediasitePlayer#
 */

/**
 * Allows for the configuration of player behavior and parts of the UI.
 * @hideconstructor
 * @extends {external:"videojs.Plugin"}
 **/
class SettingsPlugin extends videojs.getPlugin('plugin') {
    /**
     * A boolean indicating whether or not the UI should allow the user to play or pause the video.
     * @type {boolean}
     * @example
     * player.settings.enablePlayPause = true;
     */
    get enablePlayPause() {
        return _enablePlayPause.get(this) ?? true;
    }

    set enablePlayPause(enablePlayPause) {
        _enablePlayPause.set(this, enablePlayPause);
        const playButton = this.player.getDescendant('ControlBar', 'PlayToggle');
        if (enablePlayPause) {
            playButton.removeClass('vjs-hidden');
        }
        else {
            playButton.addClass('vjs-hidden');
        }
    }

    /**
     * A boolean indicating whether or not the UI should allow the user to enter full screen mode.
     * @type {boolean}
     * @example
     * player.settings.enableFullScreen = true;
     */
    get enableFullScreen() {
        return _enableFullScreen.get(this) ?? true;
    }

    set enableFullScreen(enableFullScreen) {
        _enableFullScreen.set(this, enableFullScreen);
        const fullscreenToggle = this.player.getDescendant('ControlBar', 'FullscreenToggle');
        if (enableFullScreen) {
            fullscreenToggle.removeClass('vjs-hidden');
        }
        else {
            fullscreenToggle.addClass('vjs-hidden');
        }

        if (this.player.isFullscreen()) {
            this.player.exitFullscreen();
        }
    }

    /**
     * A boolean indicating whether or not the UI should allow the user to set the playback rate.
     * @type {boolean}
     * @example
     * player.settings.enableSpeedControl = true;
     */
    get enableSpeedControl() {
        return _enableSpeedControl.get(this) ?? true;
    }

    set enableSpeedControl(enableSpeedControl) {
        _enableSpeedControl.set(this, enableSpeedControl);
        const speedControl = this.player.getDescendant('ControlBar', 'PlaybackRateMenuButton');
        if (enableSpeedControl) {
            speedControl.removeClass('vjs-hidden');
        }
        else {
            speedControl.addClass('vjs-hidden');
        }
    }

    /**
     * A boolean indicating whether or not the UI should allow the user to seek.
     * @type {boolean}
     * @example
     * player.settings.enableSeeking = true;
     */
    get enableSeeking() {
        return _enableSeeking.get(this) ?? true;
    }
    set enableSeeking(enableSeeking) {
        _enableSeeking.set(this, enableSeeking);
        const progressControl = this.player.getChild('ControlBar').getChild('ProgressControl');
        const playProgressBar = progressControl.getChild('SeekBar').getChild('PlayProgressBar');
        const timeTooltip = playProgressBar?.getChild('TimeTooltip');

        if (enableSeeking) {
            progressControl?.enable();
            playProgressBar?.el().style.setProperty('--seek-thumb-color', `#ec8b00`);
            timeTooltip?.removeClass('vjs-hidden')
        } else {
            progressControl?.disable();
            progressControl?.on(progressControl.el(), 'mousemove', progressControl.handleMouseMove);
            progressControl?.getChild('SeekBar').getChild('MouseTimeDisplay')?.show();
            playProgressBar?.el().style.setProperty('--seek-thumb-color', `transparent`);
            timeTooltip?.addClass('vjs-hidden')
        }
    }

    /**
    * A boolean indicating whether or not the UI should allow the user to seek.
    * @type {boolean}
    * @example
    * player.settings.enableSeeking = true;
    */
    get enablePIP() {
        return _enablePIP.get(this) ?? true;
    }
    set enablePIP(enablePIP) {
        _enablePIP.set(this, enablePIP);

        if (enablePIP) {
            this.player.getChild('ControlBar')?.getChild('PictureInPictureToggle')?.show();
        }
        else {
            this.player.getChild('ControlBar')?.getChild('PictureInPictureToggle')?.hide();
        }
    }

    /**
     * Gets or sets the player's background color.
     * @type {string}
     * @example
     * player.settings.backgroundColor = '#000';
     */
    get backgroundColor() {
        const computedStyle = getComputedStyle(this.player.el());
        return computedStyle.getPropertyValue('background-color');
    }

    set backgroundColor(backgroundColor) {
        this.player.el().style.backgroundColor = backgroundColor;
    }

    /**
     * Gets or sets playback rates offered in the UI.
     * @type {Array.<number>}
     * @example
     * player.settings.playbackRates = [0.5, 1.0, 2.0];
     */
    get playbackRates() {
        return _playbackRates.get(this) ?? [];
    }

    set playbackRates(rates) {
        _playbackRates.set(this, rates);
        this.player.playbackRates(rates);
    }
}

export default SettingsPlugin;
